<template>
  <OnboardingModalContent
    :title="$t('onboarding_email_subtitle')"
    data-testid="email-input-step"
  >
    <div class="tw-flex tw-flex-col tw-items-center tw-pt-8">
      <div>
        <input
          v-model="email"
          class="
            tw-text-grey-light
            tw-py-4
            tw-px-8
            tw-rounded-lg
            tw-bg-[#E1E1FF]
            tw-mb-10
            tw-email-input
            tw-placeholder-black
            focus:tw-outline-none focus:tw-placeholder-transparent
            xl:tw-text-[32px]
          "
          placeholder="___________________________"
          type="text"
        />
      </div>
      <p
        v-if="!codeSent"
        class="
          tw-text-lg tw-text-grey-light tw-font-medium tw-hidden
          xl:tw-block
        "
      >
        {{ $t('onboarding_email_message_1') }}
      </p>
      <!-- eslint-disable vue/no-v-html -->
      <p
        class="tw-text-center xl:tw-hidden"
        v-html="$t('onboarding_email_message_2')"
      />
      <!--eslint-enable-->
    </div>
    <template #bottom>
      <div>
        <div
          v-if="codeSent"
          class="tw-flex tw-flex-col tw-items-center tw-px-32 tw-mb-8"
        >
          <OnboardingField :label="$t('onboarding_insert_code')">
            <OnboardingInput
              v-model="confirmationCode"
              @input="
                (val) => (confirmationCode = String(val || '').toUpperCase())
              "
            />
          </OnboardingField>
          <Button @click="confirmCode">{{
            $t('onboarding_verify_code')
          }}</Button>
        </div>
        <div v-else class="tw-flex tw-justify-center tw-my-4">
          <Button @click="confirmEmail">{{ $t('onboarding_save') }}</Button>
        </div>
        <BottomActions
          v-if="showBackButton"
          @left-action-click="$emit('prev')"
          @right-action-click="$emit('next')"
        />
        <BottomActions v-else @right-action-click="$emit('next')" />
      </div>
    </template>
  </OnboardingModalContent>
</template>

<script>
import { mapState } from 'vuex'
import fp from 'lodash/fp'
import createInviteCodeMutation from './create_invite_code.gql'
import consumeAuthCodeMutation from './consume_auth_code.gql'
import OnboardingModalContent from './OnboardingModalContent'
import OnboardingField from '~/components/onboarding/OnboardingField'
import OnboardingInput from '~/components/onboarding/OnboardingInput'
import BottomActions from '~/components/onboarding/BottomActions'
import Button from '~/components/onboarding/Button'
import OnboardingMixin from '@/components/onboarding/OnboardingMixin'

export default {
  components: {
    BottomActions,
    OnboardingModalContent,
    OnboardingField,
    OnboardingInput,
    Button,
  },
  mixins: [OnboardingMixin],
  data() {
    return { email: '', codeSent: false, confirmationCode: '' }
  },
  computed: {
    ...mapState('fiiAuth', ['identity']),
    ...mapState('onboarding', ['emailStored']),
  },
  mounted() {
    this.email = this.emailStored || this.email
  },
  methods: {
    async confirmCode() {
      const consumeAuthCodeArgs = {
        inviteTypeName: 'ENTITY_AUTH_CLAIM',
        code: this.confirmationCode,
        entityUuid: this.identity.entityUuid,
        entityAuthClaims: this.email,
      }

      try {
        this.$emit('saving')
        this.$store.commit('onboarding/setEmail', this.email)
        await this.$apollo.mutate({
          mutation: consumeAuthCodeMutation,
          variables: { consumeAuthCodeArgs },
        })
        this.$buefy.notification.open({
          duration: 2500,
          message: this.$t('global_success'),
          type: 'is-success',
          hasIcon: true,
          position: 'is-top-right',
          autoClose: true,
        })
        this.$emit('next')
      } catch (error) {
        let message = String(error).replace('Error: GraphQL error: ', '')
        try {
          message = JSON.parse(message)
        } catch {}

        this.$buefy.notification.open({
          duration: 5000,
          message: `${this.$t('error')}: ${this.$t(message?.code + '_email')}`,
          type: 'is-danger',
          hasIcon: true,
          position: 'is-top-right',
          autoClose: true,
        })
        this.$emit('error')
      }
    },
    async confirmEmail() {
      this.$emit('saving')
      const createInviteCodeArgs = {
        inviteTypeName: 'ENTITY_AUTH_CLAIM',
        createdByEntityUuid: this.identity.entityUuid,
        invitedToEntityUuid: this.identity.entityUuid,
        inviteeEntityUuid: this.identity.entityUuid,
        data: JSON.stringify({ auth_claim: this.email }),
      }
      try {
        await this.$apollo
          .mutate({
            mutation: createInviteCodeMutation,
            variables: { createInviteCodeArgs },
          })
          .then(fp.prop('data.me.inviteCodes.createInviteCodes'))
        this.codeSent = true
        this.$emit('success')
      } catch (error) {
        let message = String(error).replace('Error: GraphQL error: ', '')
        try {
          message = JSON.parse(message)
        } catch {}

        this.$buefy.notification.open({
          duration: 5000,
          message: `${this.$t('error')}: ${this.$t(message?.code + '_email')}`,
          type: 'is-danger',
          hasIcon: true,
          position: 'is-top-right',
          autoClose: true,
        })

        this.$emit('error')
      }
    },
  },
}
</script>

<style scoped>
.email-input {
  box-shadow: 0px 0px 0px 2px #424242;
}

.email-input:focus {
  box-shadow: 0px 0px 0px 3px #4164e3;
}
</style>
